import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { DeviceEstateService } from '../../Modules/Devices/Services/device-estate.service';
import { OrganizationService } from '../../Modules/Organizations/Services/organization.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE, MAX_PAGE_SIZE } from '../../Consts/backoffice.const';
import { ROLE_NOTES } from '../../Consts/organization-detail.const';
import { DEVICE_RETIRED_MESSAGE } from '../../Consts/devices.const';
import { DeviceState } from '../../Enums/device-state.enum';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { Device } from '../../Models/backoffice/device.model';
import { DeviceDetail } from '../../Models/backoffice/device-detail.model';
import { OrganizationSummary } from '../../Models/backoffice/organization-summary.model';
import { DeviceFilters } from '../../Modules/Organizations/Interfaces/device-filters.interface';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { OrganizationListResponse } from '../../Interfaces/api/backoffice/organization.interface';
import {
  DeviceListResponse,
  DeviceShowResponse,
} from '../../Interfaces/api/backoffice/device.interface';

@Injectable()
export class DeviceEstateFacade {
  private readonly _estateService = inject(DeviceEstateService);
  private readonly _organizationService = inject(OrganizationService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _organizationId = signal<string>('');
  private readonly _selectedDeviceId = signal<string>('');
  private readonly _page = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _isMutating = signal<boolean>(false);

  private readonly _filters = signal<DeviceFilters>({ search: '', state: '' });

  private readonly _organizations = httpResource<LaravelDefaultResponse<OrganizationListResponse>>(
    () => this._organizationService.listRequest({}, FIRST_PAGE, MAX_PAGE_SIZE),
  );

  private readonly _devices = httpResource<LaravelDefaultResponse<DeviceListResponse>>(() =>
    this._organizationId()
      ? this._estateService.listRequest(
          this._organizationId(),
          this._filters(),
          this._page(),
          this._pageSize(),
        )
      : undefined,
  );

  private readonly _device = httpResource<LaravelDefaultResponse<DeviceShowResponse>>(() =>
    this._organizationId() && this._selectedDeviceId()
      ? this._estateService.showRequest(this._organizationId(), this._selectedDeviceId())
      : undefined,
  );

  readonly organizationId = computed<string>(() => this._organizationId());

  readonly organizations = computed<OrganizationSummary[]>(
    () => this._organizationService.toPagedResult(this._resourceState.valueOf(this._organizations)).items,
  );

  readonly organizationName = computed<string>(
    () =>
      this.organizations().find((organization) => organization.id === this._organizationId())
        ?.displayName ?? '',
  );

  readonly filters = computed<DeviceFilters>(() => this._filters());

  readonly page = computed<number>(() => this._page());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly devices = computed<Device[]>(() => this._estateService.toDevices(this._resourceState.valueOf(this._devices)));

  readonly counts = computed<Record<string, number>>(() =>
    this._estateService.toCounts(this._resourceState.valueOf(this._devices)),
  );

  readonly total = computed<number>(() => this._estateService.toTotal(this._resourceState.valueOf(this._devices)));

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._devices.status()),
  );

  readonly selectedDeviceId = computed<string>(() => this._selectedDeviceId());

  readonly selectedDevice = computed<DeviceDetail | null>(() =>
    this._estateService.toDetail(this._resourceState.valueOf(this._device)),
  );

  readonly selectedDeviceState = computed<MetricState>(() =>
    this._resourceState.metricState(this._device.status()),
  );

  readonly hasOrganization = computed<boolean>(() => this._organizationId() !== '');

  readonly reapableCount = computed<number>(() => this.counts()[DeviceState.REAPABLE] ?? 0);

  readonly inactiveCount = computed<number>(() => this.counts()[DeviceState.INACTIVE] ?? 0);

  readonly hasActiveFilters = computed<boolean>(() =>
    Object.values(this._filters()).some((value) => value !== ''),
  );

  readonly canRetire = computed<boolean>(
    () => this._operatorFacade.atLeast(ROLE_NOTES) && !this._isMutating(),
  );

  selectOrganization(organizationId: string): void {
    this._organizationId.set(organizationId);
    this._selectedDeviceId.set('');
    this._page.set(FIRST_PAGE);
  }

  selectDevice(deviceId: string): void {
    this._selectedDeviceId.set(deviceId);
  }

  applyFilters(filters: DeviceFilters): void {
    this._filters.set(filters);
    this._page.set(FIRST_PAGE);
  }

  clearFilters(): void {
    this.applyFilters({ search: '', state: '' });
  }

  showReapable(): void {
    this.applyFilters({ ...this._filters(), state: DeviceState.REAPABLE });
  }

  goToPage(page: number): void {
    this._page.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._page.set(FIRST_PAGE);
  }

  reload(): void {
    this._devices.reload();
    this._device.reload();
  }

  retire(deviceId: string, reason: string): void {
    if (!this.canRetire()) {
      return;
    }

    this._isMutating.set(true);

    this._estateService
      .retire(this._organizationId(), deviceId, reason)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(),
        error: (error) => this._failMutation(error),
      });
  }

  private _afterWrite(): void {
    this._isMutating.set(false);
    this.reload();
    this._msgService.showSnackBar(DEVICE_RETIRED_MESSAGE, { type: 'success' });
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
